import {
  SipRecommendationActionType,
  SipRecommendationType,
  type SipIdealAllocation
} from '../api/common.types'
import type {
  SipIdealSip,
  SipIncludedFund,
  SipPlansOverviewCategory,
  SipPlansOverviewResponse
} from '../api/overview.types'
import type {
  SipDecreaseRecommendation,
  SipIncreaseRecommendation,
  SipPlansCategory,
  SipStartRecommendation,
  SipStopRecommendation
} from '../api/sipPlan.types'
import { formatTargetSip } from '../../display-values/sipPlanAmount'
import { roundToNearestRupee } from '../../display-values/lumpsumPlanAmount'

export type IdealSipNoActionFund = SipIdealSip & {
  recommendedSipAmount: number
  currentSipAmount: number
  powerupSipAmount?: number | null
  externalSipAmount?: number | null
  infiniteSipAmount?: number | null
}

export type DerivedIdealSip =
  | SipStartRecommendation
  | SipIncreaseRecommendation
  | SipDecreaseRecommendation
  | IdealSipNoActionFund

export type DerivedIdealCategory = {
  categoryId?: string | null
  categoryName?: string | null
  idealAllocationPercentage?: SipIdealAllocation | null
  idealSips: DerivedIdealSip[]
  hasCategoryError: boolean
}

export type DerivedYourPlanCategory = SipPlansCategory & {
  hasCategoryError: boolean
}

export type DerivedSipPlansView = {
  idealCategories: DerivedIdealCategory[]
  yourPlanCategories: DerivedYourPlanCategory[]
  actionableFundCount: number
}

/** Absolute ₹ tolerance: treat current as no-action when within this of recommended. */
export const SIP_NO_ACTION_AMOUNT_TOLERANCE_RUPEES = 1

export function deriveRecommendedSipAmount(
  totalIdealSipAmount: number,
  idealAllocationPercentage?: SipIdealAllocation | null
): number {
  const absolutePercent = idealAllocationPercentage?.absolute ?? 0
  if (totalIdealSipAmount <= 0 || absolutePercent <= 0) {
    return 0
  }
  const rawAmount = (totalIdealSipAmount * absolutePercent) / 100
  // Same rupee amount the SIP screens show. No nearest-100 snap.
  return formatTargetSip({ rawAmount }).value
}

export function isSipAmountWithinNoActionTolerance(
  currentSipAmount: number,
  recommendedSipAmount: number
): boolean {
  if (recommendedSipAmount <= 0) {
    return currentSipAmount === 0
  }
  return Math.abs(currentSipAmount - recommendedSipAmount) <= SIP_NO_ACTION_AMOUNT_TOLERANCE_RUPEES
}

/** Summary may send currentSipAmount or totalSipAmount (same value). Nearest rupee, same as the SIP badge. */
function resolveIncludedFundCurrentSipAmount(
  includedFund: SipIncludedFund | null | undefined
): number {
  const rawAmount = includedFund?.currentSipAmount ?? includedFund?.totalSipAmount ?? 0
  return roundToNearestRupee(rawAmount)
}

export function deriveSipPlansView(
  overview?: SipPlansOverviewResponse | null
): DerivedSipPlansView {
  const categories = overview?.categories ?? []
  const totalIdealSipAmount = overview?.totalIdealSipAmount ?? 0

  const idealCategories: DerivedIdealCategory[] = []
  const yourPlanCategories: DerivedYourPlanCategory[] = []
  let actionableFundCount = 0

  for (const category of categories) {
    const hasCategoryError = isSipCategoryDataError(category)
    const derivedIdealSips = buildDerivedIdealSips(
      category?.idealSips,
      category?.includedFunds,
      totalIdealSipAmount
    )

    idealCategories.push({
      categoryId: category?.categoryId,
      categoryName: category?.categoryName,
      idealAllocationPercentage: category?.idealAllocationPercentage,
      idealSips: derivedIdealSips,
      hasCategoryError
    })

    if (hasCategoryError) {
      yourPlanCategories.push({
        categoryId: category?.categoryId,
        categoryName: category?.categoryName,
        group: category?.group,
        idealAllocationPercentage: category?.idealAllocationPercentage,
        stopRecommendations: [],
        startRecommendations: [],
        increaseRecommendations: [],
        decreaseRecommendations: [],
        noActionFunds: [],
        hasCategoryError: true
      })
      continue
    }

    const yourPlanCategory = buildDerivedYourPlanCategory(
      category,
      totalIdealSipAmount
    )
    actionableFundCount +=
      (yourPlanCategory.stopRecommendations?.length ?? 0) +
      (yourPlanCategory.startRecommendations?.length ?? 0) +
      (yourPlanCategory.increaseRecommendations?.length ?? 0) +
      (yourPlanCategory.decreaseRecommendations?.length ?? 0)

    yourPlanCategories.push(yourPlanCategory)
  }

  return {
    idealCategories,
    yourPlanCategories,
    actionableFundCount
  }
}

function isSipCategoryDataError(category: SipPlansOverviewCategory | null | undefined): boolean {
  const includedFunds = category?.includedFunds
  const idealSips = category?.idealSips
  // One-sided empty arrays are valid: included-only → stop all, ideal-only → start all.
  if (!Array.isArray(includedFunds) && !Array.isArray(idealSips)) {
    return true
  }
  return false
}

function buildDerivedIdealSips(
  idealSips: SipIdealSip[] | null | undefined,
  includedFunds: SipIncludedFund[] | null | undefined,
  totalIdealSipAmount: number
): DerivedIdealSip[] {
  if (!Array.isArray(idealSips)) {
    return []
  }

  const includedFundByIsin = new Map(
    (includedFunds ?? []).map((includedFund) => [includedFund?.isin ?? '', includedFund])
  )

  return idealSips.map((idealSip) => {
    const recommendedSipAmount = deriveRecommendedSipAmount(
      totalIdealSipAmount,
      idealSip?.idealAllocationPercentage
    )
    const includedFund = includedFundByIsin.get(idealSip?.isin ?? '') ?? null
    const currentSipAmount = resolveIncludedFundCurrentSipAmount(includedFund)

    if (!includedFund || currentSipAmount <= 0) {
      if (includedFund) {
        return buildStartRecommendation(includedFund, idealSip, totalIdealSipAmount)
      }
      return buildStartRecommendationFromIdealOnly(idealSip, totalIdealSipAmount)
    }

    if (isSipAmountWithinNoActionTolerance(currentSipAmount, recommendedSipAmount)) {
      return {
        ...idealSip,
        recommendedSipAmount,
        currentSipAmount,
        powerupSipAmount: includedFund?.powerupSipAmount ?? 0,
        externalSipAmount: includedFund?.externalSipAmount ?? 0,
        infiniteSipAmount: includedFund?.infiniteSipAmount ?? 0
      }
    }

    if (currentSipAmount < recommendedSipAmount) {
      return buildIncreaseRecommendation(includedFund, idealSip, recommendedSipAmount)
    }

    return buildDecreaseRecommendation(includedFund, idealSip, recommendedSipAmount)
  })
}

function buildDerivedYourPlanCategory(
  category: SipPlansOverviewCategory,
  totalIdealSipAmount: number
): DerivedYourPlanCategory {
  const includedFunds = category?.includedFunds ?? []
  const idealSips = category?.idealSips ?? []
  const idealSipByIsin = new Map(
    idealSips.map((idealSip) => [idealSip?.isin ?? '', idealSip])
  )
  const includedFundByIsin = new Map(
    includedFunds.map((includedFund) => [includedFund?.isin ?? '', includedFund])
  )

  const stopRecommendations: SipStopRecommendation[] = []
  const startRecommendations: SipStartRecommendation[] = []
  const increaseRecommendations: SipIncreaseRecommendation[] = []
  const decreaseRecommendations: SipDecreaseRecommendation[] = []
  const noActionFunds: SipIncludedFund[] = []

  for (const includedFund of includedFunds) {
    const isin = includedFund?.isin ?? ''
    const idealSip = idealSipByIsin.get(isin)
    const currentSipAmount = resolveIncludedFundCurrentSipAmount(includedFund)

    if (!idealSip) {
      stopRecommendations.push(
        buildStopRecommendation(includedFund)
      )
      continue
    }

    if (currentSipAmount === 0) {
      startRecommendations.push(
        buildStartRecommendation(includedFund, idealSip, totalIdealSipAmount)
      )
      continue
    }

    const recommendedSipAmount = deriveRecommendedSipAmount(
      totalIdealSipAmount,
      idealSip?.idealAllocationPercentage
    )

    if (isSipAmountWithinNoActionTolerance(currentSipAmount, recommendedSipAmount)) {
      noActionFunds.push(buildNoActionFund(includedFund, idealSip))
    } else if (currentSipAmount < recommendedSipAmount) {
      increaseRecommendations.push(
        buildIncreaseRecommendation(includedFund, idealSip, recommendedSipAmount)
      )
    } else {
      decreaseRecommendations.push(
        buildDecreaseRecommendation(includedFund, idealSip, recommendedSipAmount)
      )
    }
  }

  for (const idealSip of idealSips) {
    const isin = idealSip?.isin ?? ''
    if (includedFundByIsin.has(isin)) {
      continue
    }
    startRecommendations.push(
      buildStartRecommendationFromIdealOnly(idealSip, totalIdealSipAmount)
    )
  }

  return {
    categoryId: category?.categoryId,
    categoryName: category?.categoryName,
    group: category?.group,
    idealAllocationPercentage: category?.idealAllocationPercentage,
    stopRecommendations,
    startRecommendations,
    increaseRecommendations,
    decreaseRecommendations,
    noActionFunds,
    hasCategoryError: false
  }
}

function buildStopRecommendation(includedFund: SipIncludedFund): SipStopRecommendation {
  return {
    ...includedFund,
    currentSipAmount: resolveIncludedFundCurrentSipAmount(includedFund),
    recommendedSipAmount: 0,
    actionType: SipRecommendationActionType.STOP,
    recommendationType: SipRecommendationType.ACTIONABLE,
    recommendationStatus: includedFund?.recommendationStatus ?? null,
    recommendationStatusReason: includedFund?.recommendationStatusReason ?? null,
    recommendationStatusDisplayText: includedFund?.recommendationStatusDisplayText ?? ''
  }
}

function buildStartRecommendation(
  includedFund: SipIncludedFund,
  idealSip: SipIdealSip,
  totalIdealSipAmount: number
): SipStartRecommendation {
  return {
    ...includedFund,
    ...pickIdealSipInsightFields(idealSip),
    currentSipAmount: resolveIncludedFundCurrentSipAmount(includedFund),
    recommendedSipAmount: deriveRecommendedSipAmount(
      totalIdealSipAmount,
      idealSip?.idealAllocationPercentage
    ),
    actionType: SipRecommendationActionType.START,
    recommendationType: SipRecommendationType.ACTIONABLE,
    recommendationStatus: idealSip?.recommendationStatus ?? null,
    recommendationStatusReason: idealSip?.recommendationStatusReason ?? null,
    recommendationStatusDisplayText: idealSip?.recommendationStatusDisplayText ?? ''
  }
}

function buildStartRecommendationFromIdealOnly(
  idealSip: SipIdealSip,
  totalIdealSipAmount: number
): SipStartRecommendation {
  return {
    ...idealSip,
    currentSipAmount: 0,
    recommendedSipAmount: deriveRecommendedSipAmount(
      totalIdealSipAmount,
      idealSip?.idealAllocationPercentage
    ),
    actionType: SipRecommendationActionType.START,
    recommendationType: SipRecommendationType.ACTIONABLE
  }
}

function buildIncreaseRecommendation(
  includedFund: SipIncludedFund,
  idealSip: SipIdealSip,
  recommendedSipAmount: number
): SipIncreaseRecommendation {
  return {
    ...includedFund,
    ...pickIdealSipInsightFields(idealSip),
    currentSipAmount: resolveIncludedFundCurrentSipAmount(includedFund),
    recommendedSipAmount,
    actionType: SipRecommendationActionType.INCREASE,
    recommendationType: SipRecommendationType.ACTIONABLE,
    recommendationStatus: idealSip?.recommendationStatus ?? null,
    recommendationStatusReason: idealSip?.recommendationStatusReason ?? null,
    recommendationStatusDisplayText: idealSip?.recommendationStatusDisplayText ?? ''
  }
}

function buildDecreaseRecommendation(
  includedFund: SipIncludedFund,
  idealSip: SipIdealSip,
  recommendedSipAmount: number
): SipDecreaseRecommendation {
  return {
    ...includedFund,
    ...pickIdealSipInsightFields(idealSip),
    currentSipAmount: resolveIncludedFundCurrentSipAmount(includedFund),
    recommendedSipAmount,
    actionType: SipRecommendationActionType.DECREASE,
    recommendationType: SipRecommendationType.ACTIONABLE,
    recommendationStatus: idealSip?.recommendationStatus ?? null,
    recommendationStatusReason: idealSip?.recommendationStatusReason ?? null,
    recommendationStatusDisplayText: idealSip?.recommendationStatusDisplayText ?? ''
  }
}

function buildNoActionFund(
  includedFund: SipIncludedFund,
  idealSip: SipIdealSip
): SipIncludedFund {
  return {
    ...includedFund,
    ...pickIdealSipInsightFields(idealSip),
    currentSipAmount: resolveIncludedFundCurrentSipAmount(includedFund)
  }
}

function pickIdealSipInsightFields(idealSip: SipIdealSip) {
  return {
    idealAllocationPercentage: idealSip?.idealAllocationPercentage ?? null,
    underPerformance: idealSip?.underPerformance ?? false,
    highExposure: idealSip?.highExposure ?? false,
    overExposure: idealSip?.overExposure ?? false,
    overDiversification: idealSip?.overDiversification ?? false
  }
}

/*
  How a SIP fund is placed in a bucket
  =====================================
  This file turns one SIP summary response into two lists: Your Plan and Ideal.
  A fund is not given a bucket by the backend. The bucket is decided here.

  Your Plan buckets (what the Your Plan tab shows)
  -------------------------------------------------
  stopRecommendations      Stop. The fund is in the portfolio and is not in the ideal plan.
  startRecommendations     Start. The fund is in the ideal plan and the current SIP is 0,
                           or the fund exists only on the ideal side.
  increaseRecommendations  Increase. Current SIP is below the recommended SIP.
  decreaseRecommendations  Decrease. Current SIP is above the recommended SIP.
  noActionFunds            No action. Current SIP matches the recommended SIP.

  actionableFundCount is stop + start + increase + decrease.
  noActionFunds is not included in that count.

  Keys that decide the bucket
  ---------------------------
  Match the two sides by isin.
    includedFunds[].isin  ===  idealSips[].isin
  Same ISIN means it is the same fund. Fund name, fundMetadata, category, and group
  are not used for the bucket.

  Current SIP is read only from the included fund, in this order:
    includedFunds[].currentSipAmount
    if that is missing, includedFunds[].totalSipAmount
    if both are missing, 0
  That amount is then rounded to the nearest rupee before the bucket check.
  idealSips[].currentSipAmount is ignored. Do not use it to decide the bucket.

  Recommended SIP is read only from the ideal fund:
    overview.totalIdealSipAmount
    idealSips[].idealAllocationPercentage.absolute
  The category's own idealAllocationPercentage is for the category header.
  It is not the fund's recommended SIP.

  Recommended SIP math
  --------------------
  raw = totalIdealSipAmount * absolute / 100
  recommended = that raw amount rounded to the nearest rupee

  This is the same amount the SIP screens show. There is no nearest-100 snap.
  2750 stays 2750. It does not become 2800.

  If totalIdealSipAmount is 0 or missing, recommended is 0.
  If absolute is 0 or missing, recommended is 0.

  No-action tolerance
  -------------------
  The fund is no action when the absolute gap is 1 rupee or less:
    abs(current - recommended) <= 1
  A gap of 1 rupee is no action, including a 1 rupee increase.
  Current 2749 against recommended 2750 is no action.
  A gap of 2 rupees is not no action.
  If recommended is 0, no action is only when current is also exactly 0.
  That 0-versus-0 case is rarely reached on Your Plan, because a current of
  exactly 0 is classified as Start before this tolerance is checked.

  Your Plan decision order
  ------------------------
  Walk includedFunds first. The first matching rule wins.

  1. Category data error
     includedFunds is not an array AND idealSips is not an array.
     Every bucket is empty and hasCategoryError is true.
     An empty array is valid. Included-only means every fund is Stop.
     Ideal-only means every fund is Start.

  2. Stop
     This included fund has no ideal SIP with the same isin.
     Current amount does not matter. Even a current of 0 is Stop
     when the fund is absent from idealSips.

  3. Start, because current is exactly 0
     The isin is on both sides, and the included current is exactly 0.
     This is checked before the tolerance, so current 0 is Start
     even when the recommended SIP is also 0.

  4. No action
     abs(current - recommended) <= 1

  5. Increase
     current < recommended, and the gap is more than 1 rupee.

  6. Decrease
     current > recommended, and the gap is more than 1 rupee.

  After every included fund is placed, walk idealSips.
  An ideal SIP whose isin is not in includedFunds is Start.
  Its current SIP is treated as 0. This is the "fund is only in the ideal plan" case.

  Ideal tab is not the same walk
  ------------------------------
  The Ideal tab only walks idealSips. A fund that exists only in includedFunds
  does not appear there, so Stop is a Your Plan bucket only.

  On the Ideal tab, missing included fund OR current <= 0 is Start.
  Your Plan uses current === 0, so only an exact 0 is Start there.
  After that, Ideal uses the same tolerance, then Increase, then Decrease.

  Worked example
  --------------
  totalIdealSipAmount = 55000
  included fund isin INF789F1AWB9, no currentSipAmount, totalSipAmount = 2750
  ideal SIP same isin, absolute = 5

  Current = 2750, because currentSipAmount is missing and totalSipAmount is used.
  Recommended = 55000 * 5 / 100 = 2750
  The screen shows ₹2,750. The bucket uses 2750 as well.
  Gap = 0, which is within 1
  The Your Plan bucket is No action.

  Same numbers with totalSipAmount = 2748 would be Increase.
  Same numbers with totalSipAmount = 2800 would be Decrease.
  If that isin were missing from idealSips, it would be Stop, whatever the amount is.
  If that isin were missing from includedFunds, it would be Start.
*/
